import { useEffect, useRef, useState } from 'react';
import { formatTime } from '../lib/format.js';
import { isWaxing, moonLitPath } from '../lib/moon.js';
import { useEtMinutes } from '../lib/use-et-minutes.js';

// The day at a glance, drawn from the brief's own numbers: the hourly tide
// curve, the high and low times, sunrise and sunset, the moon, and the three
// dayparts the plan below is told in. Nothing here is written by the model.
//
// Added 2026-09-27 after the owner, looking at the all-type front page, asked
// "what about some graphics". A photograph would need a daily source nothing
// produces (design review 2026-09-26, considered and rejected); a tide and
// daylight strip is an almanac staple, and it shows the thing the plan keeps
// reasoning about ("the afternoon low opens wider sand") as a picture.
//
// Drawn at the container's real pixel width rather than scaled through a
// viewBox, so the 11px labels stay 11px on a phone.

// Palette tokens (tailwind.config.js). SVG attributes, not classes, so the
// drawing does not depend on which utilities the build happened to keep.
const INK = '#16263c'; // navy-900
const LINE = '#3f6694'; // navy-500
const LABEL = '#30517a'; // navy-600
const QUIET = '#3f6694'; // navy-500
const WATER = '#dde7f1'; // navy-100
const NIGHT = '#f1f5fa'; // navy-50
const GUIDE = '#bccde2'; // navy-200
const SAND = '#f1e2c0'; // cream-200
const GOLD = '#cf9844'; // cream-500
const GOLD_TEXT = '#8a6a1b'; // cream-600
const PAPER = '#fdfaf3'; // cream-50
const MOON_DARK = '#274263'; // navy-700
const MOON_LIT = '#e6cd92'; // cream-300: warm enough to read as a moon, not an O
const FONT = 'Inter, system-ui, -apple-system, sans-serif';

const START = 6 * 60; // 6 AM
const END = 22 * 60; // 10 PM
const H = 132;
const TOP = 24; // top of the water
const AXIS = 88; // the baseline the water sits on
const ROW2 = 106;
const ROW3 = 124;

function toMin(hhmm) {
  const m = typeof hhmm === 'string' ? hhmm.match(/^(\d{1,2}):(\d{2})/) : null;
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}

function useWidth(ref) {
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(Math.round(el.getBoundingClientRect().width));
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return width;
}

// Catmull-Rom through the hourly points, as cubic Beziers: a smooth tide line
// that still passes through every NOAA reading.
function smoothPath(pts) {
  if (pts.length < 2) return '';
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6];
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6];
    d += ` C ${c1[0].toFixed(1)} ${c1[1].toFixed(1)}, ${c2[0].toFixed(1)} ${c2[1].toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  return d;
}

// The drawn curve's y at pixel x, from the same Catmull-Rom segment the path
// uses, so the "now" dot sits on the line and not a pixel beside it. The
// readings are hourly and evenly spaced, so a segment's x runs linearly
// with its parameter.
function curveYAt(pts, px) {
  for (let i = 0; i < pts.length - 1; i++) {
    const p1 = pts[i];
    const p2 = pts[i + 1];
    if (px < p1[0] || px > p2[0]) continue;
    const p0 = pts[i - 1] || p1;
    const p3 = pts[i + 2] || p2;
    const c1 = p1[1] + (p2[1] - p0[1]) / 6;
    const c2 = p2[1] - (p3[1] - p1[1]) / 6;
    const s = (px - p1[0]) / (p2[0] - p1[0] || 1);
    const u = 1 - s;
    return u * u * u * p1[1] + 3 * u * u * s * c1 + 3 * u * s * s * c2 + s * s * s * p2[1];
  }
  return null;
}

function clockLabel(min) {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return formatTime(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
}

// Rounded to the quarter hour for the widest-beach window: it is a two-hour
// rule of thumb either side of the low, not a prediction to the minute.
function quarterLabel(min) {
  return clockLabel(Math.round(min / 15) * 15).replace(':00', '');
}

// `live` is true only on today's edition of the homepage, where the strip
// marks the present minute on the curve. An archived day has no "now".
export default function DayStrip({ tides, sunrise, sunset, moon, live = false }) {
  const ref = useRef(null);
  const W = useWidth(ref);
  const nowMin = useEtMinutes(live);
  const curve = Array.isArray(tides?.curve) ? tides.curve : [];
  const rise = toMin(sunrise);
  const set = toMin(sunset);
  if (curve.length < 2 || rise == null || set == null) return null;

  const x = (min) => ((min - START) / (END - START)) * W;
  const points = curve
    .map((p) => ({ min: toMin(p.time), h: p.height }))
    .filter((p) => p.min != null && typeof p.h === 'number');
  const hMin = Math.min(0, ...points.map((p) => p.h));
  const hMax = Math.max(...points.map((p) => p.h));
  const y = (h) => AXIS - ((h - hMin) / (hMax - hMin || 1)) * (AXIS - TOP);
  const inView = points.filter((p) => p.min >= START - 60 && p.min <= END + 60);
  const pts = inView.map((p) => [x(p.min), y(p.h)]);
  const line = smoothPath(pts);
  const area = line ? `${line} L ${pts[pts.length - 1][0].toFixed(1)} ${AXIS} L ${pts[0][0].toFixed(1)} ${AXIS} Z` : '';

  const events = (tides.events || [])
    .map((e) => ({ type: e.type === 'high' ? 'high' : 'low', min: toMin(e.time), h: e.height }))
    .filter((e) => e.min != null && e.min >= START && e.min <= END);

  // Where the beach is widest: two hours either side of the lowest daylight
  // low, inside daylight. The same rule the synthesis worker hands the model.
  const daylightLows = events.filter((e) => e.type === 'low' && e.min >= rise && e.min <= set);
  const low = daylightLows.sort((a, b) => a.h - b.h)[0];
  const sand = low ? { from: Math.max(low.min - 120, rise), to: Math.min(low.min + 120, set) } : null;

  // The plan's dayparts (netlify/functions/lib/dayparts.js): morning from half
  // an hour before sunrise, afternoon from noon, evening from 5 PM.
  const parts = [
    ['Morning', Math.max(rise - 30, START), 12 * 60],
    ['Afternoon', 12 * 60, 17 * 60],
    ['Evening', 17 * 60, Math.min(set + 150, END)]
  ];

  const anchorFor = (px) => (px < 56 ? 'start' : px > W - 56 ? 'end' : 'middle');
  // Rough Inter width at 11px, for keeping top-row labels apart. On a phone
  // the band label drops its hours (the band itself shows them) so the high
  // tides either side keep theirs.
  const textW = (str) => str.length * 6.2;
  const sandText = sand
    ? W < 480
      ? 'Widest beach'
      : `Widest beach ${quarterLabel(sand.from)} to ${quarterLabel(sand.to)}`
    : null;
  const sandX = sand ? x((sand.from + sand.to) / 2) : null;
  const f = typeof moon?.illumination === 'number' ? moon.illumination / 100 : null;
  const phase = String(moon?.phase || '');
  const waxing = isWaxing(phase);
  const moonX = f != null && W - x(set) > 36 ? Math.min(W - 12, (x(set) + W) / 2 + 10) : null;

  // Now, on today's edition: a dot on the curve and a sounding line from it
  // down to the baseline, so the strip answers "where are we in the tide" at
  // a glance. The label tries the sunrise and sunset row first, then the
  // space above the dot, and is left off rather than printed over another
  // label. On a phone the sunset label covers most of the afternoon's row.
  const nowX = nowMin != null && nowMin >= START && nowMin <= END ? x(nowMin) : null;
  const nowY = nowX != null ? curveYAt(pts, nowX) : null;
  const nowLabelY = (() => {
    if (nowX == null || nowY == null) return null;
    // Horizontal and vertical extents of every other label and mark, as
    // [left, right, top, bottom].
    const box = (px, py, str, anchor) => {
      const w = textW(str);
      const left = anchor === 'start' ? px : anchor === 'end' ? px - w : px - w / 2;
      return [left, left + w, py - 10, py + 2];
    };
    const taken = [
      box(x(rise) - 4, ROW2, `Sunrise ${clockLabel(rise)}`, 'start'),
      box(x(set) + 4, ROW2, `Sunset ${clockLabel(set)}`, 'end'),
      moonX != null ? [moonX - 6, moonX + 6, ROW2 - 10, ROW2 + 2] : null,
      sand ? box(sandX, 12, sandText, 'middle') : null,
      ...events.flatMap((e) => {
        const px = x(e.min);
        const py = y(e.h);
        const ty = e.type === 'high' ? Math.max(12, py - 9) : Math.max(28, py - 20);
        return [
          box(px, ty, `${e.type === 'high' ? 'High' : 'Low'} ${clockLabel(e.min)}`, anchorFor(px)),
          [px - 4, px + 4, py - 4, py + 4]
        ];
      })
    ].filter(Boolean);
    const fits = (ly) => {
      const [l, r, t, b] = box(nowX, ly, 'Now', anchorFor(nowX));
      // textW already runs wide of Inter, so 3px of air is enough.
      return ly > 10 && !taken.some(([l2, r2, t2, b2]) => l - 3 < r2 && r + 3 > l2 && t < b2 && b > t2);
    };
    return [ROW2, nowY - 9].find(fits) ?? null;
  })();

  const summary = [
    ...events.map((e) => `${e.type} tide ${clockLabel(e.min)}`),
    `sunrise ${clockLabel(rise)}`,
    `sunset ${clockLabel(set)}`,
    sand ? `widest beach about ${quarterLabel(sand.from)} to ${quarterLabel(sand.to)}` : null,
    f != null ? `${phase ? `${phase} moon` : 'moon'}, ${moon.illumination}% lit` : null
  ].filter(Boolean);

  return (
    <figure ref={ref} className="mb-5 mt-1" style={{ height: H }}>
      {W > 0 && (
        <svg width={W} height={H} role="img" aria-label={`Today's tide and daylight: ${summary.join(', ')}.`}>
          {/* The dark hours, before sunrise and after sunset. */}
          <rect x={0} y={TOP - 14} width={Math.max(0, x(rise))} height={AXIS - TOP + 14} fill={NIGHT} />
          <rect x={x(set)} y={TOP - 14} width={Math.max(0, W - x(set))} height={AXIS - TOP + 14} fill={NIGHT} />

          {/* The widest beach, behind the water. */}
          {sand && (
            <>
              <rect x={x(sand.from)} y={TOP - 14} width={x(sand.to) - x(sand.from)} height={AXIS - TOP + 14} fill={SAND} opacity={0.75} />
              <text x={sandX} y={12} textAnchor="middle" fontFamily={FONT} fontSize={11} fontWeight={500} fill={GOLD_TEXT}>
                {sandText}
              </text>
            </>
          )}

          {/* Daypart boundaries: the same three parts the plan uses. */}
          {[12 * 60, 17 * 60].map((m) => (
            <line key={m} x1={x(m)} x2={x(m)} y1={TOP - 6} y2={AXIS} stroke={GUIDE} strokeDasharray="2 3" />
          ))}

          {/* The water. On first paint the line draws itself in from the
              morning and the water fills in behind it (index.css), once. */}
          <path className="day-strip-fill" d={area} fill={WATER} opacity={0.85} />
          <path className="day-strip-line" pathLength={1} d={line} fill="none" stroke={LINE} strokeWidth={1.5} />
          <line x1={0} x2={W} y1={AXIS} y2={AXIS} stroke={INK} strokeWidth={1} />

          {nowX != null && nowY != null && (
            <g>
              <line x1={nowX} x2={nowX} y1={nowY} y2={AXIS} stroke={INK} strokeWidth={1} opacity={0.45} />
              {/* A ring that widens and fades three times after the line has
                  drawn in, then rests. */}
              <circle className="day-strip-ping" cx={nowX} cy={nowY} r={4.5} fill="none" stroke={GOLD} strokeWidth={1.5} />
              <circle cx={nowX} cy={nowY} r={4.5} fill={GOLD} stroke={PAPER} strokeWidth={1.5} />
              {nowLabelY != null && (
                // A paper halo, so the word stays legible where it sits over
                // the water or the tide line.
                <text
                  x={nowX}
                  y={nowLabelY}
                  textAnchor={anchorFor(nowX)}
                  fontFamily={FONT}
                  fontSize={11}
                  fontWeight={600}
                  fill={INK}
                  stroke={PAPER}
                  strokeWidth={3}
                  strokeLinejoin="round"
                  paintOrder="stroke"
                >
                  Now
                </text>
              )}
            </g>
          )}

          {/* Highs and lows. */}
          {events.map((e, i) => {
            const px = x(e.min);
            const py = y(e.h);
            // A low sits in a dip, and the water rises either side of it, so
            // its label goes higher than a high's does or the line runs
            // through the text on a phone.
            const ty = e.type === 'high' ? Math.max(12, py - 9) : Math.max(28, py - 20);
            // A high's label shares the top row with the widest-beach label;
            // skip it rather than print one over the other.
            const text = `${e.type === 'high' ? 'High' : 'Low'} ${clockLabel(e.min)}`;
            const clash =
              sand && e.type === 'high' && Math.abs(px - sandX) < textW(sandText) / 2 + textW(text) / 2 + 8;
            return (
              <g key={i}>
                <circle cx={px} cy={py} r={3} fill={INK} stroke={PAPER} strokeWidth={1.5} />
                {!clash && (
                  <text x={px} y={ty} textAnchor={anchorFor(px)} fontFamily={FONT} fontSize={11} fill={LABEL}>
                    {text}
                  </text>
                )}
              </g>
            );
          })}

          {/* Sunrise and sunset on the baseline. */}
          {[
            [rise, 'Sunrise', 'start'],
            [set, 'Sunset', 'end']
          ].map(([m, word, anchor]) => {
            const px = x(m);
            return (
              <g key={word}>
                <circle cx={px} cy={AXIS} r={3.5} fill={GOLD} />
                {[0, 45, 90, 135, 180].map((deg) => {
                  const a = (deg * Math.PI) / 180;
                  return (
                    <line
                      key={deg}
                      x1={px + Math.cos(a) * 5.5}
                      y1={AXIS - Math.sin(a) * 5.5}
                      x2={px + Math.cos(a) * 8}
                      y2={AXIS - Math.sin(a) * 8}
                      stroke={GOLD}
                      strokeWidth={1.2}
                      strokeLinecap="round"
                    />
                  );
                })}
                <text x={anchor === 'start' ? px - 4 : px + 4} y={ROW2} textAnchor={anchor} fontFamily={FONT} fontSize={11} fill={LABEL}>
                  {word} {clockLabel(m)}
                </text>
              </g>
            );
          })}

          {/* Tonight's moon, in the dark hours. */}
          {moonX != null && (
            <g transform={`translate(${moonX}, ${ROW2 - 4})`}>
              <circle r={6} fill={MOON_DARK} />
              <path d={moonLitPath(0, 0, 6, Math.max(0, Math.min(1, f)), waxing)} fill={MOON_LIT} />
              <circle r={6} fill="none" stroke={MOON_DARK} strokeWidth={0.75} />
            </g>
          )}

          {/* The dayparts, named under the line. */}
          {parts.map(([name, a, b]) => (
            <text key={name} x={x((a + b) / 2)} y={ROW3} textAnchor="middle" fontFamily={FONT} fontSize={11} fontWeight={600} fill={QUIET} letterSpacing="0.04em">
              {name}
            </text>
          ))}
        </svg>
      )}
    </figure>
  );
}
